import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { 
  fetchUserDropdowns, 
    selectLastUserDropdownFetch,
  selectUserDropdowns, 
  selectDropdownLoading, 
  selectDropdownError 
} from "../redux/slices/dropdownSlice";

/**
 * Custom hook to fetch and access user contacts organized by role
 * Uses the same Redux store as useUserDropdowns to prevent duplicate API calls
 * 
 * IMPORTANT: This hook now uses Redux to cache data and prevent race conditions.
 * The data is fetched from the same source as useUserDropdowns but formatted differently.
 * Multiple components can use this hook without causing duplicate MS Graph API calls.
 * 
 * Returns data in format:
 * {
 *   projectManager: [{ id, name, email }],
 *   mechanicalEngineer: [{ id, name, email }],
 *   electricalEngineer: [{ id, name, email }],
 *   controlsEngineer: [{ id, name, email }],
 *   sales: [{ id, name, email }]
 * }
 */
const useUserContact = () => {
    const dispatch = useDispatch();
    const { instance, accounts } = useMsal();
    
    // Get data from Redux store (same source as useUserDropdowns)
    const userDropdowns = useSelector(selectUserDropdowns);
    const lastUserDropdownFetch = useSelector(selectLastUserDropdownFetch);
    const loading = useSelector(selectDropdownLoading);
    const error = useSelector(selectDropdownError);

    // Fetch user dropdowns on mount if MSAL is ready
    // Redux will handle caching and prevent duplicate calls
    useEffect(() => {
        if (instance && accounts && accounts.length > 0 && !lastUserDropdownFetch) {
            dispatch(fetchUserDropdowns({ instance, accounts }));
        }
    }, [dispatch, instance, accounts, lastUserDropdownFetch]);

    // Transform the Redux data to match the expected format for useUserContact
    // Map displayName/email format to id/name/email format
    const userContacts = useMemo(() => {
        const transformMembers = (members) => {
            if (!Array.isArray(members)) return [];
            return members.map((member) => ({
                id: member.id || member.email, // Use email as fallback ID
                name: member.displayName || "N/A",
                email: member.email || "N/A",
            }));
        };

        return {
            projectManager: transformMembers(userDropdowns.appsOptions),
            mechanicalEngineer: transformMembers(userDropdowns.mechOptions),
            electricalEngineer: transformMembers(userDropdowns.elecOptions),
            controlsEngineer: transformMembers(userDropdowns.controlOptions),
            sales: transformMembers(userDropdowns.salesOptions),
        };
    }, [userDropdowns]);

    return { 
        userContacts, 
        loading, 
        error: error ? String(error) : null 
    };
};

export default useUserContact;
